import React, { useState, useEffect, useRef } from "react";
import CheckBoxSelect from "./CheckBoxSelect";
import {
	NavigationSharp,
	ApartmentSharp,
	StoreSharp,
	PostAddSharp,
} from "@mui/icons-material";
import { RiResetLeftLine } from "react-icons/ri";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";

const compareArrays = (arr1 = [], arr2 = []) => {
	if (arr1.length !== arr2.length) return true;
	const set1 = new Set(arr1);
	return arr2.some((item) => !set1.has(item));
};

const SearchHeader = ({
	setReset,
	setSelectedCountryData,
	selectedCountryData,
	country,
	setSelectedCity,
	selectedCity,
	city,
	setSelectedCompanyData,
	selectedCompanyData,
	companyList,
	setSelectedDesignationData,
	selectedDesignationData,
	designationList,
	onResetFilters,
}) => {
	const [filtersChanged, setFiltersChanged] = useState(false);
	const [initialValues, setInitialValues] = useState({
		country: [],
		city: [],
		company: [],
		designation: [],
	});
	const hasUserModified = useRef(false);
	const { t } = useTranslation();

	const countryOptions =
		country?.map((val) => ({
			label: `${val.name} [${val.itemCounts}]`,
			value: val.id,
		})) || [];
	const cityOptions =
		city?.map((val) => ({
			label: `${val.name} [${val.itemCounts}]`,
			value: val.id,
		})) || [];
	const companyListOptions =
		companyList?.map((val) => ({
			label: `${val.name} [${val.itemCounts}]`,
			value: val.id,
		})) || [];
	const designationListOptions =
		designationList?.map((val) => ({
			label: `${val.name} [${val.itemCounts}]`,
			value: val.id,
		})) || [];

	// Snapshot the first full lists for reset comparison / reset target
	useEffect(() => {
		if (
			countryOptions.length &&
			cityOptions.length &&
			companyListOptions.length &&
			designationListOptions.length &&
			initialValues.country.length === 0
		) {
			setInitialValues({
				country: countryOptions.map((c) => c.value),
				city: cityOptions.map((c) => c.value),
				company: companyListOptions.map((c) => c.value),
				designation: designationListOptions.map((c) => c.value),
			});
		}
	}, [countryOptions, cityOptions, companyListOptions, designationListOptions, initialValues.country.length]);

	useEffect(() => {
		if (!hasUserModified.current) return;
		const isFilterChanged =
			compareArrays(selectedCountryData, initialValues.country) ||
			compareArrays(selectedCity, initialValues.city) ||
			compareArrays(selectedCompanyData, initialValues.company) ||
			compareArrays(selectedDesignationData, initialValues.designation);

		setFiltersChanged(isFilterChanged);
	}, [
		selectedCountryData,
		selectedCity,
		selectedCompanyData,
		selectedDesignationData,
		initialValues,
	]);

	// Country → clear City / Company / Designation (re-filled as "all" of the new dependent lists)
	const handleCountryChange = (val) => {
		hasUserModified.current = true;
		setSelectedCountryData(val);
		setSelectedCity([]);
		setSelectedCompanyData([]);
		setSelectedDesignationData([]);
		setReset(true);
	};

	// City → clear Company / Designation
	const handleCityChange = (val) => {
		hasUserModified.current = true;
		setSelectedCity(val);
		setSelectedCompanyData([]);
		setSelectedDesignationData([]);
		setReset(true);
	};

	// Company → clear Designation
	const handleCompanyChange = (val) => {
		hasUserModified.current = true;
		setSelectedCompanyData(val);
		setSelectedDesignationData([]);
		setReset(true);
	};

	const handleDesignationChange = (val) => {
		hasUserModified.current = true;
		setSelectedDesignationData(val);
		setReset(true);
	};

	return (
		<div className="d-flex flex-wrap jobSearch jobdropdown wlj-job-filter-row mb-2">
			<div className="search-dropdown wlj-ddl-cell">
				<CheckBoxSelect
					icon={<NavigationSharp className="material-symbols-sharp wlj-ddl-icon" />}
					placeholder={t("SearchJob.Select Country")}
					options={countryOptions}
					selectedValues={selectedCountryData}
					onChange={handleCountryChange}
				/>
			</div>
			<div className="search-dropdown wlj-ddl-cell">
				<CheckBoxSelect
					icon={<ApartmentSharp className="material-symbols-sharp wlj-ddl-icon" />}
					placeholder={t("SearchJob.Select City")}
					options={cityOptions}
					selectedValues={selectedCity}
					onChange={handleCityChange}
				/>
			</div>
			<div className="search-dropdown wlj-ddl-cell">
				<CheckBoxSelect
					icon={<StoreSharp className="material-symbols-sharp wlj-ddl-icon" />}
					placeholder={t("SearchJob.Select Company")}
					options={companyListOptions}
					selectedValues={selectedCompanyData}
					onChange={handleCompanyChange}
				/>
			</div>
			<div className="search-dropdown wlj-ddl-cell">
				<CheckBoxSelect
					icon={<PostAddSharp className="material-symbols-sharp wlj-ddl-icon" />}
					placeholder={t("SearchJob.Select Designation")}
					options={designationListOptions}
					selectedValues={selectedDesignationData}
					onChange={handleDesignationChange}
				/>
			</div>
			<div className="clearFilter wlj-ddl-reset">
				<div className="clear-all-filter-cont">
					<Tooltip title={t("SearchJob.Reset")} placement="bottom">
						<span>
							<RiResetLeftLine
								style={{
									opacity: filtersChanged ? 1 : 0.5,
									color: filtersChanged ? "var(--text-color2)" : "gray",
									fontSize: 24,
									cursor: filtersChanged ? "pointer" : "default",
								}}
								onClick={() => {
									if (!filtersChanged) return;
									if (typeof onResetFilters === "function") {
										onResetFilters();
									} else {
										setSelectedCountryData(initialValues.country);
										setSelectedCity(initialValues.city);
										setSelectedCompanyData(initialValues.company);
										setSelectedDesignationData(initialValues.designation);
										setReset(true);
									}
									hasUserModified.current = false;
									setFiltersChanged(false);
								}}
							/>
						</span>
					</Tooltip>
				</div>
			</div>
		</div>
	);
};

export default SearchHeader;
